import React, { useState, useEffect, useCallback } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './ModuleAdmin.css';
import GamificationModal from './GamificationModal';
import BbManageModal from './BbManageModal';
import UpdateEducatorsModal from './UpdateEducatorsModal';
import ModuleTopicsTargetsModal from './ModuleTopicsTargetsModal';

// Helper function to group modules by organization and course
const groupModulesByOrganization = (moduleSemesters) => {
  const grouped = {};
  
  if (!moduleSemesters || !Array.isArray(moduleSemesters)) {
    return {};
  }
  
  moduleSemesters.forEach(ms => {
    if (!ms) return;
    
    // Use the actual property names from the API response
    const organization = ms.Organization || 'test';
    const course = ms.Course || 'Unknown Course';
    const key = `${organization} - ${course}`;
    
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(ms);
  });
  
  return grouped;
};

/**
 * Module Admin Component - Enhanced with Bootstrap 5
 */
const ModuleAdmin = ({ 
  fullPageMode = false, 
  token, 
  userUtilities, 
  moduleSemesters = []
}) => {
  // State variables
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [showGamificationModal, setShowGamificationModal] = useState(false);
  const [showUpdateEducatorsModal, setShowUpdateEducatorsModal] = useState(false);
  const [showBbManageModal, setShowBbManageModal] = useState(false);
  const [showTopicsTargetsModal, setShowTopicsTargetsModal] = useState(false);
  
  // Other modal data states
  const [gamificationData, setGamificationData] = useState(null);
  const [topicsTargetsData, setTopicsTargetsData] = useState(null);
  const [bbCourseData, setBbCourseData] = useState(null);
  const [activeModuleSemesterId, setActiveModuleSemesterId] = useState(null);

  // Debug what React receives
  useEffect(() => {
    console.log('=== REACT DEBUG: Props Received ===');
    console.log('fullPageMode:', fullPageMode);
    console.log('token:', token ? 'provided' : 'missing');
    console.log('userUtilities:', userUtilities ? 'provided' : 'missing');
    console.log('moduleSemesters type:', typeof moduleSemesters);
    console.log('moduleSemesters isArray:', Array.isArray(moduleSemesters));
    console.log('moduleSemesters length:', moduleSemesters?.length);
    console.log('moduleSemesters data:', moduleSemesters);
  }, []);

  // ✅ Enhanced debugging for the actual data structure
  useEffect(() => {
    console.log('=== ModuleAdmin Component Debug Info ===');
    console.log('Props received:', {
      fullPageMode,
      token: token ? 'provided' : 'missing',
      userUtilities: userUtilities ? 'provided' : 'missing',
      moduleSemesters: {
        type: typeof moduleSemesters,
        isArray: Array.isArray(moduleSemesters),
        length: moduleSemesters?.length || 0
      }
    });
    
    if (moduleSemesters && moduleSemesters.length > 0) {
      console.log('First item sample:', moduleSemesters[0]);
      console.log('Available properties:', Object.keys(moduleSemesters[0]));
      console.log('Data appears to be classes, not module semesters');
    }
    console.log('==========================================');
  }, [fullPageMode, token, userUtilities, moduleSemesters]);

  // Helper function to check if user is coordinator
  const isUserCoordinator = useCallback((msId) => {
    if (userUtilities && typeof userUtilities.isCoordinator === 'function') {
      return userUtilities.isCoordinator(msId);
    }
    return false;
  }, [userUtilities]);

  // Event handlers - Updated to work with module semester IDs
  const handleLessonPlan = useCallback((e, moduleSemesterInfo) => {
    e.preventDefault();
    console.log('Lesson Plan clicked for:', moduleSemesterInfo);
    // Navigate to lesson plan using ModuleSemesterID - fixed format to match LessonPlan page
    window.location.href = `#LessonPlans/${moduleSemesterInfo.ID}`;
  }, []);

  const handleClasses = useCallback((e, moduleSemesterInfo) => {
    e.preventDefault();
    console.log('Classes clicked for:', moduleSemesterInfo);
    // Navigate to classes using ModuleSemesterID - fixed format to match hash parser
    window.location.href = `#Classes/${moduleSemesterInfo.ID}`;
  }, []);

  const handleModuleSettings = useCallback((e, moduleSemesterInfo) => {
    e.preventDefault();
    console.log('Module Settings clicked for:', moduleSemesterInfo);
    
        setTopicsTargetsData({
      moduleSemesterId: moduleSemesterInfo.ID,
      isCoordinator: isUserCoordinator(moduleSemesterInfo.ID),
      classes: [],
      topics: []
    });
    setShowTopicsTargetsModal(true);
  }, [isUserCoordinator]);

  const handleGamification = useCallback((e, ms) => {
    e.preventDefault();
    console.log('Opening gamification modal for:', ms.ID);
    const tokenForRequest = getTokenForRequest();
    if (window.OrganizationUtilities) {
      const helper = new window.OrganizationUtilities();
      helper.getClasses(ms.ID, tokenForRequest,
        (classes) => {
          console.log('Classes loaded for gamification:', classes);
          setGamificationData({
            moduleSemesterId: ms.ID,
            classes: classes || []
          });
          setShowGamificationModal(true);
        },
        (status, xhr) => {
          console.error('Error loading classes:', status);
          if (window.CheckForAuthError && window.CheckForAuthError(status)) {
            return;
          }
          setError(status.statusText || 'Failed to load classes for gamification');
        }
      );
    } else {
      setError('OrganizationUtilities not available');
    }
  }, []);

  const handleFileSelect = useCallback((e, moduleSemesterInfo) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected for module semester:', moduleSemesterInfo.ID, 'File:', file.name);
    }
  }, []);

  const handleUpload = useCallback(async (moduleSemesterInfo) => {
    const fileInput = document.getElementById(`file-${moduleSemesterInfo.ID}`);
    const file = fileInput?.files[0];
    
    if (!file) {
      alert('Please select a file first');
      return;
    }

    // Validate file type (should be Excel file)
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      alert('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      const formData = new FormData();
      // Append file with name 'fileupload' (must match server-side Request.Files[0] expectation)
      // Include filename as third parameter for .NET compatibility
      formData.append('fileupload', file, file.name);

      console.log(`Uploading invitation code file: ${file.name} for module: ${moduleSemesterInfo.ID}`);
      console.log('File size:', file.size, 'bytes');
      console.log('File type:', file.type);
      
      // Ensure cookies are sent with the request (required for authentication)
      // Note: Do NOT set Content-Type header manually - browser will set it with boundary for multipart/form-data
      const response = await fetch(`./DownloadInvitationCode.aspx?msId=${moduleSemesterInfo.ID}`, {
        method: 'POST',
        body: formData,
        credentials: 'include', // Important: Send cookies for authentication
        // Don't set Content-Type - let browser set it automatically with boundary
      });
      
      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      // Always read the response to check for errors
      const responseClone = response.clone();
      const responseText = await responseClone.text();
      console.log('Response text (first 500 chars):', responseText.substring(0, 500));

      // Check response status first
      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        
        if (responseText) {
          if (responseText.includes('Not authorized')) {
            errorMessage = 'Not authorized. Please check your session and try again.';
          } else if (responseText.includes('runtime error') || responseText.includes('Exception') || responseText.includes('Error')) {
            // Extract error details from server response
            errorMessage = `Server error: ${responseText.substring(0, 500)}`;
          } else if (responseText.trim().length > 0) {
            errorMessage = responseText.trim().substring(0, 500);
          }
        }
        
        throw new Error(errorMessage);
      }

      // Get content type and disposition
      const contentType = response.headers.get('content-type') || '';
      const contentDisposition = response.headers.get('content-disposition') || '';
      
      // Check if response is an error message (text/html or small size)
      if (contentType.includes('text/html') || contentType.includes('text/plain')) {
        // Likely an error page or error message
        if (responseText.includes('runtime error') || responseText.includes('Exception') || responseText.includes('Error') || responseText.includes('Not authorized')) {
          throw new Error(responseText.substring(0, 1000) || 'Server returned an error');
        }
      }
      
      // Get blob for download
      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'bytes');
      
      // Check if blob is small (likely an error message)
      if (blob.size < 500) {
        if (responseText && (responseText.includes('Not authorized') || responseText.includes('error') || responseText.includes('Error') || responseText.includes('Exception') || responseText.includes('runtime error') || responseText.trim().length < 100)) {
          throw new Error(responseText.trim() || 'Server returned an error message');
        }
      }

      // Get filename from Content-Disposition header or use default
      let filename = 'Student_Invite_Codes.xls';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '').trim();
        }
      }

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('Invitation codes generated successfully! File downloaded.');

      // Reset file input
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      console.error('Error uploading invitation code file:', err);
      alert(`Error: ${err.message || 'Failed to upload invitation code file. Please try again.'}`);
    }
  }, []);

  const handleUpdateEducators = useCallback((moduleSemesterInfo) => {
    console.log('Update educators clicked for module semester:', moduleSemesterInfo.ID);
    setModalData({ 
      moduleSemester: moduleSemesterInfo, 
      token 
    });
    setShowUpdateEducatorsModal(true);
  }, [token]);

  const handleBbManage = useCallback((moduleSemesterInfo) => {
    if (!moduleSemesterInfo.BbCourse) {
      console.error('No BbCourse data available');
      return;
    }
    console.log('Opening BB Manage modal for module semester:', moduleSemesterInfo.ID);
    setBbCourseData(moduleSemesterInfo.BbCourse);
    setShowBbManageModal(true);
  }, []);

  // Handle modal closes
  const handleCloseGamificationModal = () => {
    setShowGamificationModal(false);
    setGamificationData(null);
  };

  const handleCloseBbManageModal = () => {
    setShowBbManageModal(false);
    setBbCourseData(null);
  };
  
  const handleCloseUpdateEducatorsModal = () => {
    setShowUpdateEducatorsModal(false);
    setModalData(null);
  };
  
  const handleCloseTopicsTargetsModal = () => {
    setShowTopicsTargetsModal(false);
    setTopicsTargetsData(null);
  };

  // Render module groups function with correct property names
  const renderModuleGroups = () => {
    console.log('renderModuleGroups called with data:', moduleSemesters);
    
    if (!moduleSemesters || !Array.isArray(moduleSemesters) || moduleSemesters.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-inbox fa-3x mb-3 text-secondary"></i>
          <p className="h5">No module semesters found</p>
          <p>There are no editable module semesters available.</p>
        </div>
      );
    }
    
    const grouped = groupModulesByOrganization(moduleSemesters);
    console.log('Grouped modules:', grouped);
    
    return Object.entries(grouped).map(([moduleKey, modules]) => (
      <div key={moduleKey} className="mb-4">
        {/* Organization/Course Header */}
        <div className="organization-header bg-light border border-bottom-0 px-3 py-2 fw-semibold text-dark small">
          {moduleKey}
        </div>
        
        {/* Table */}
        <div className="table-responsive">
          <table className="table table-bordered mb-0 bg-white module-admin-table">
            <thead className="table-light">
              <tr>
                <th scope="col" className="fw-semibold text-dark small">Module</th>
                <th scope="col" className="fw-semibold text-dark small">Semester</th>
                <th scope="col" className="fw-semibold text-dark small text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((ms) => (
                <tr key={ms.ID}>
                  <td className="align-middle">
                    <div className="d-flex align-items-center">
                      <span className="fw-medium text-dark small">
                        {/* Use the actual properties available in the data */}
                        {ms.Module || ms.ModuleName || ms.Course || ms.Name || 'N/A'}
                      </span>
                      {ms.BbCourse && ms.BbCourse.Origin && (
                        <span className="blackboard-tag ms-2">
                          {ms.BbCourse.Origin}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="align-middle">
                    <span className="text-muted small">
                      {/* Use the actual properties available in the data */}
                      {ms.Semester || ms.SemesterName || ms.Term || 'N/A'}
                    </span>
                  </td>
                  <td className="align-middle">
                    <div className="d-flex flex-nowrap justify-content-center action-buttons" style={{ gap: '2px' }}>
                      {/* Action Links */}
                      <a 
                        href="#" 
                        className="action-link"
                        onClick={(e) => handleLessonPlan(e, ms)}
                      >
                        Lesson Plan
                      </a>
                      
                      <a 
                        href="#" 
                        className="action-link"
                        onClick={(e) => handleClasses(e, ms)}
                      >
                        Classes
                      </a>
                      
                      <a 
                        href="#" 
                        className="action-link"
                        onClick={(e) => handleModuleSettings(e, ms)}
                      >
                        Module Settings
                      </a>
                      
                      <a 
                        href="#" 
                        className="action-link"
                        onClick={(e) => handleGamification(e, ms)}
                      >
                        Gamification
                      </a>
                      
                      {/* File Upload Section */}
                      {/* <div className="d-flex align-items-center gap-1" style={{ display: 'none' }}>
                        <div className="file-input-wrapper">
                          <input 
                            type="file" 
                            className="file-input" 
                            id={`file-${ms.ID}`}
                            accept=".xlsx,.xls"
                            onChange={(e) => handleFileSelect(e, ms)}
                          />
                          <label 
                            htmlFor={`file-${ms.ID}`} 
                            className="btn btn-outline-secondary btn-sm"
                            style={{ margin: 0 }}
                          >
                            Choose File
                          </label>
                        </div>
                        
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleUpload(ms)}
                          style={{ margin: 0 }}
                        >
                          Upload
                        </button>
                      </div> */}
                      
                      {/* Update Educators Button */}
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleUpdateEducators(ms)}
                        style={{ margin: 0 }}
                      >
                        Update Educators
                      </button>
                      
                      {/* Manage External Sign Up Button - Only for Blackboard courses */}
                      {ms.BbCourse && ms.BbCourse.Origin === "Blackboard" && (
                        <button 
                          className="btn btn-outline-purple btn-sm"
                          onClick={() => handleBbManage(ms)}
                          title="Manage External Sign Up"
                          style={{ margin: 0 }}
                        >
                          <i className="fas fa-globe me-1"></i>
                          Manage External Sign Up
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ));
  };

  // Main render logic for full page mode
  if (fullPageMode) {
    return (
      <div className="container-fluid px-0">
        {/* Page Header */}
        <div className="row">
          <div className="col-12">
            <h2 className="h3 mb-4 pb-2 border-bottom border-2 border-secondary text-dark fw-semibold">
              Module Admin
            </h2>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            </div>
          </div>
        )}

        {/* Module Data */}
        <div className="row">
          <div className="col-12">
            {renderModuleGroups()}
          </div>
        </div>

        {/* Modals */}
        {showGamificationModal && gamificationData && (
        <GamificationModal
          data={gamificationData}
          onClose={handleCloseGamificationModal}
        />
      )}
      
        {showUpdateEducatorsModal && modalData && (
          <UpdateEducatorsModal 
            data={modalData} 
            onClose={handleCloseUpdateEducatorsModal} 
          />
        )}
        
        {showBbManageModal && bbCourseData && (
        <BbManageModal
            data={bbCourseData} 
          onClose={handleCloseBbManageModal}
        />
      )}
      
        {showTopicsTargetsModal && topicsTargetsData && (
          <ModuleTopicsTargetsModal 
            data={topicsTargetsData} 
            onClose={handleCloseTopicsTargetsModal} 
          />
        )}
      </div>
    );
  }

  // Modal mode (when used as a popup/modal component)
  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Module Admin</h5>
        </div>
      <div className="card-body">
        {/* Your modal-specific content here */}
        <p>Module Admin modal content goes here</p>
          </div>
    </div>
  );
};

export default ModuleAdmin; 